import React from 'react';
import PropTypes from 'prop-types';

const Card = ({
  children,
  title,
  subtitle,
  actions,
  className = '',
  bodyClassName = '',
  headerClassName = '',
  footerClassName = '',
  footer,
  shadow = 'md',
  padding = 'md',
  hover = false,
  onClick,
}) => {
  const shadowClasses = {
    none: '',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
  };

  const paddingClasses = {
    none: '',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
    xl: 'p-8',
  };

  const baseClasses = `bg-white rounded-lg border border-gray-200 ${shadowClasses[shadow]} ${
    hover ? 'hover:shadow-lg transition-shadow duration-200' : ''
  } ${onClick ? 'cursor-pointer' : ''} ${className}`;

  return (
    <div className={baseClasses} onClick={onClick}>
      {/* Header */}
      {(title || subtitle || actions) && (
        <div
          className={`border-b border-gray-200 ${paddingClasses[padding]} ${headerClassName}`}
        >
          <div className="flex items-center justify-between">
            <div>
              {title && (
                <h3 className="text-lg font-medium text-[#601e00]">{title}</h3>
              )}
              {subtitle && (
                <p className="mt-1 text-sm text-gray-600">{subtitle}</p>
              )}
            </div>
            {actions && (
              <div className="flex items-center space-x-2">{actions}</div>
            )}
          </div>
        </div>
      )}

      {/* Body */}
      <div className={`${paddingClasses[padding]} ${bodyClassName}`}>
        {children}
      </div>

      {/* Footer */}
      {footer && (
        <div
          className={`border-t border-gray-200 ${paddingClasses[padding]} ${footerClassName}`}
        >
          {footer}
        </div>
      )}
    </div>
  );
};

Card.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  actions: PropTypes.node,
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
  headerClassName: PropTypes.string,
  footerClassName: PropTypes.string,
  footer: PropTypes.node,
  shadow: PropTypes.oneOf(['none', 'sm', 'md', 'lg', 'xl']),
  padding: PropTypes.oneOf(['none', 'sm', 'md', 'lg', 'xl']),
  hover: PropTypes.bool,
  onClick: PropTypes.func,
};

// Specialized Card Components
export const StatsCard = ({
  title,
  value,
  change,
  changeType,
  icon,
  className = '',
}) => {
  const changeColorClasses = {
    positive: 'text-green-600',
    negative: 'text-red-600',
    neutral: 'text-gray-600',
  };

  return (
    <Card className={`${className}`} padding="md">
      <div className="flex items-center">
        <div className="flex-1">
          <p className="text-sm font-medium text-[#601e00]">{title}</p>
          <p className="text-2xl font-semibold text-[#601e00]">{value}</p>
          {change && (
            <p
              className={`text-sm ${changeColorClasses[changeType] || changeColorClasses.neutral}`}
            >
              {change}
            </p>
          )}
        </div>
        {icon && (
          <div className="flex-shrink-0">
            <div className="w-8 h-8 text-gray-400">{icon}</div>
          </div>
        )}
      </div>
    </Card>
  );
};

StatsCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  change: PropTypes.string,
  changeType: PropTypes.oneOf(['positive', 'negative', 'neutral']),
  icon: PropTypes.node,
  className: PropTypes.string,
};

export const ActionCard = ({
  title,
  description,
  actions,
  icon,
  className = '',
}) => {
  return (
    <Card className={`${className}`} padding="md" hover>
      <div className="flex items-start">
        {icon && (
          <div className="flex-shrink-0 mr-4">
            <div className="w-10 h-10 text-brand-yellow">{icon}</div>
          </div>
        )}
        <div className="flex-1">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          {description && (
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          )}
          {actions && <div className="mt-3 flex space-x-2">{actions}</div>}
        </div>
      </div>
    </Card>
  );
};

ActionCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  actions: PropTypes.node,
  icon: PropTypes.node,
  className: PropTypes.string,
};

export default Card;
